/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "components/Input";
import { useAppContext } from "contexts";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import Button from "components/Button";
import { FormProvider, useForm } from "react-hook-form";
import Select from "react-select";
import {
  AlertTriangle,
  CalendarDays,
  ArrowRight,
  Tag,
  Clock,
  Wallet,
  UserCheck,
  Briefcase,
} from "lucide-react";
import apiService from "services/apiService";
import { UpdatePayload } from "services/Leave/leaveInterface";
import leaveManager from "services/Leave/leaveManager";
import { getYear, getMonthByDate, convert, dateFormate } from "util/Util";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";
import * as yup from "yup";

// Leave balance (GET api/leave-balance/me) — drives the leave-type options and
// the over-balance warning for the employee's own leave.
interface LeaveBalance {
  id: number;
  userId: number;
  leavePolicyId: number;
  leaveType: string;
  balance: number;
  lastAccrualDate: string;
  month: number;
  year: number;
}

// Clean day formatting: "1", "8", "0.5", "1.5" (trailing zeros trimmed).
const formatDays = (value: number): string => String(parseFloat((Number(value) || 0).toFixed(2)));

const validationSchema = yup.object({
  leaveFrom: yup.string().required("Leave From is required"),
  leaveTo: yup.string().required("Leave To is required"),
  days: yup.string(),
  leaveType: yup.string().required("Leave Type is required"),
  reason: validateNoTrailingOrLeadingWhitespace("Leave Reason").required("Leave Reason is required"),
  status: yup.string(),
  comment: yup.string(),
});

interface IProps {
  onUpdate: () => void;
  selectedRow: any;
  onClose: () => void;
  onError: (msg?: string) => void;
}

const leaveForOptions = [
  { value: 1, label: "Full Day" },
  { value: 0.5, label: "Half Day" },
];
const data1 = [
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Submitted", label: "Submitted" },
];

const leaveTypeOptions = [
  { value: "Sick leave", label: "Sick Leave" },
  { value: "Urgent leave", label: "Urgent leave" },
  { value: "Maternity and paternity leave", label: "Maternity and paternity leave" },
  { value: "Public and religious holidays", label: "Public and religious holidays" },
  { value: "Bereavement leave", label: "Bereavement leave" },
  { value: "Time off in lieu (TOIL)", label: "Time off in lieu (TOIL)" },
  { value: "Unpaid leave", label: "Unpaid leave" },
  { value: "Paid leave", label: "Paid leave" },
  { value: "Other leave", label: "Other leave" },
];

// Added standard padding (!px-3 !py-2.5) and minimum height (!min-h-[42px]) to restore vertical size
const sharedInputClassNames = "!w-full !px-3 !py-2.5 !min-h-[42px] !rounded-lg !bg-gray-50 dark:!bg-gray-700 !border-gray-300 dark:!border-gray-600 !text-gray-900 dark:!text-white disabled:!cursor-not-allowed disabled:!opacity-100";

// ── Read-only presentation (admin/approve view) ─────────────────────────────
const getInitials = (name?: string): string =>
  name?.trim()
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("")
    : "–";

const statusClasses = (status: string): string => {
  switch ((status || "").toLowerCase()) {
    case "approved":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/30 dark:text-emerald-400";
    case "submitted":
    case "pending":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/30 dark:text-amber-400";
    case "rejected":
      return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/30 dark:text-rose-400";
    default:
      return "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300";
  }
};

// Compact label/value block for the read-only summary.
const ReadItem: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <div className="min-w-0">
    <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
      {icon}
      {label}
    </p>
    <p className="mt-0.5 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
      {value ?? "—"}
    </p>
  </div>
);

const LeaveDetails = ({ onUpdate, selectedRow, onClose, onError }: IProps) => {
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { ...selectedRow },
  });

  const [leaveFrom, setLeaveFrom] = useState("");
  const [leaveTo, setLeaveTo] = useState("");
  const [days, setDays] = useState(0);
  const [selectedLeaveType, setSelectedLeaveType] = useState("Sick leave");
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [selected, setSelected] = useState<any>();
  // LOP / deductible decision — defaults to true; admin can toggle when the
  // leave carries a balanceWarning. Sent in the update payload.
  const [isDeductible, setIsDeductible] = useState<boolean>(true);
  // Toggling isDeductible lives outside RHF, so track it to enable the submit
  // button even when no other field changed.
  const [deductibleTouched, setDeductibleTouched] = useState(false);
  const { userRole, user } = useAppContext();

  const [balances, setBalances] = useState<LeaveBalance[]>([]);

  // The /me balance belongs to the signed-in user, so the balance-driven leave
  // types + warning only apply when editing one's own leave.
  const isOwnLeave = !!user?.email && selectedRow?.email === user.email;

  // Leave balance for the current month/year.
  useEffect(() => {
    if (!isOwnLeave) return;
    const today = new Date();
    apiService
      .get(`api/leave-balance/me?month=${today.getMonth() + 1}&year=${today.getFullYear()}`)
      .then((res) => setBalances(Array.isArray(res.data) ? res.data : []))
      .catch((error) => {
        console.log("Failed to fetch leave balance:", error);
        setBalances([]);
      });
  }, [isOwnLeave]);

  // Leave-type options come from the balance response (fallback: static list),
  // always including the record's current type so it stays selectable.
  const balanceOptions = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();
    balances.forEach((b) => {
      if (b.leaveType && !map.has(b.leaveType)) {
        map.set(b.leaveType, { value: b.leaveType, label: b.leaveType });
      }
    });
    return Array.from(map.values());
  }, [balances]);

  const leaveTypeSelectOptions = useMemo(() => {
    const base = isOwnLeave && balanceOptions.length ? balanceOptions : leaveTypeOptions;
    if (selectedLeaveType && !base.some((o) => o.value === selectedLeaveType)) {
      return [{ value: selectedLeaveType, label: selectedLeaveType }, ...base];
    }
    return base;
  }, [isOwnLeave, balanceOptions, selectedLeaveType]);

  // Available balance for the selected type + over-limit flag (non-blocking).
  const selectedBalance = isOwnLeave
    ? balances.find((b) => b.leaveType === selectedLeaveType)
    : undefined;
  const availableDays = selectedBalance ? Number(selectedBalance.balance) : null;
  const overLimit = availableDays != null && days > availableDays;

  const {
    formState: { isDirty, isSubmitting },
  } = methods;

  const onSubmit = async (data: any) => {
    onClose();
    // Year & month must come from the leave's own end date, not "today". A leave
    // applied in Dec 2026 for Jan 2027 has to be tagged to 2027 so it stays
    // consistent with the month and shows under the correct year filter.
    const leaveEndDate = data.leaveTo || selectedRow?.leaveTo;
    const payload: UpdatePayload = {
      id: selectedRow?.id,
      leaveTo: data.leaveTo || selectedRow?.leaveTo,
      leaveFrom: data.leaveFrom || selectedRow?.leaveFrom,
      unit: days,
      reason: data.reason || selectedRow?.reason,
      leaveType: data.leaveType || selectedRow?.leaveType,
      status: data.status || selectedRow?.status,
      department: selectedRow?.department,
      comment: comment,
      year: (leaveEndDate ? new Date(leaveEndDate).getFullYear() : getYear()) || selectedRow?.year,
      month: getMonthByDate(leaveEndDate) || selectedRow?.month,
      email: data.email || selectedRow?.email,
      leaveAppliedDate: selectedRow?.leaveAppliedDate,
      // Send the admin's LOP choice for flagged leaves; otherwise pass through.
      isDeductible: showDeductible ? isDeductible : selectedRow?.isDeductible,
    };
    leaveManager
      .updateLeave(payload)
      .then(() => { onUpdate(); })
      .catch((err: any) => {
        console.error("Update leave error:", err);

        let errorMsg = "Something went wrong";

        if (err?.response?.data) {
          const responseData = err.response.data;

          if (typeof responseData === "string") {
            // If the backend sends a plain text string
            errorMsg = responseData;
          } else if (typeof responseData === "object") {
            // If the backend sends a JSON object, check common keys 
            // or stringify the whole object so it shows in the toaster exactly as it does in the preview
            errorMsg =
              responseData.message ||
              responseData.error ||
              responseData.details ||
              JSON.stringify(responseData);
          }
        } else if (err?.message) {
          // Fallback for network-level errors (e.g., "Network Error", "Timeout")
          errorMsg = err.message;
        }

        onError(errorMsg);
      });
  };

  useEffect(() => {
    if (!selectedRow) return;
    const initialLeaveFrom = convert(selectedRow.leaveFrom);
    const initialLeaveTo = convert(selectedRow.leaveTo);
    const initialDays = Number(selectedRow.unit || 0);
    const initialReason = selectedRow.reason || "";
    const initialComment = selectedRow.comment || "";
    const initialLeaveType = selectedRow.leaveType || "Sick leave";
    const initialStatus = selectedRow.status || "Submitted";

    setLeaveFrom(initialLeaveFrom);
    setLeaveTo(initialLeaveTo);
    setDays(initialDays);
    setReason(initialReason);
    setComment(initialComment);
    setSelectedLeaveType(initialLeaveType);
    setSelected(data1.find((option) => option.value === initialStatus));

    methods.reset({
      ...selectedRow,
      leaveFrom: initialLeaveFrom,
      leaveTo: initialLeaveTo,
      days: initialDays.toString(),
      reason: initialReason,
      comment: initialComment,
      leaveType: initialLeaveType,
      status: initialStatus,
    });
  }, [selectedRow, methods]);

  useEffect(() => {
    if (!leaveFrom || !leaveTo) return;
    const initialFrom = selectedRow ? convert(selectedRow.leaveFrom) : null;
    const initialTo = selectedRow ? convert(selectedRow.leaveTo) : null;
    if (leaveFrom === initialFrom && leaveTo === initialTo) return;

    const convertLeaveFrom = new Date(leaveFrom);
    const convertLeaveTo = new Date(leaveTo);
    let count = 0;
    for (
      let d = new Date(convertLeaveFrom);
      d <= convertLeaveTo;
      d.setDate(d.getDate() + 1)
    ) {
      if (d.getDay() !== 6 && d.getDay() !== 0) count++;
    }
    setDays(count);
    methods.setValue("days", count.toString());
  }, [leaveFrom, leaveTo]);

  const onLeaveFromChanged = (e: any) => {
    const value = e.target.value;
    setLeaveFrom(value);
    methods.setValue("leaveFrom", value, { shouldDirty: true, shouldValidate: true });
  };

  const onLeaveToChanged = (e: any) => {
    const value = e.target.value;
    setLeaveTo(value);
    methods.setValue("leaveTo", value, { shouldDirty: true, shouldValidate: true });
  };

  const onLeaveTypeChanged = (selectedOption: any) => {
    methods.clearErrors("leaveType");
    setSelectedLeaveType(selectedOption.value);
    methods.setValue("leaveType", selectedOption.value, { shouldDirty: true, shouldValidate: true });
  };

  const onDaysChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = parseFloat(e.target.value);
    setDays(value);
    methods.setValue("days", value.toString());
  };

  // Leave For (Full/Half) is only meaningful for a single-day leave. Picking it
  // sets the unit: Half = 0.5, Full = 1 day.
  const onLeaveForChanged = (selectedOption: any) => {
    if (!selectedOption) return;
    const unit = selectedOption.value === 0.5 ? 0.5 : 1;
    setDays(unit);
    methods.setValue("days", unit.toString(), { shouldDirty: true, shouldValidate: true });
  };

  const handleReasonChange = (e: any) => {
    const value = e.target.value;
    setReason(value);
    methods.setValue("reason", value, { shouldDirty: true, shouldValidate: true });
  };

  const handleCommentChange = (e: any) => {
    const value = e.target.value;
    setComment(value);
    methods.setValue("comment", value, { shouldDirty: true, shouldValidate: true });
  };

  const isEmployeeView =
    (userRole === 2 && selectedRow.email !== user.email) || userRole === 3;
  const isAdminView =
    (userRole === 2 || userRole === 3) && selectedRow.email !== user.email;
  // Only leaves flagged with a balance warning expose the LOP/deductible control.
  const hasBalanceWarning = !!selectedRow?.balanceWarning;
  const showDeductible = isAdminView && hasBalanceWarning;

  const minLeaveToDate = leaveFrom
    ? new Date(leaveFrom).toISOString().split("T")[0]
    : "";
  const maxLeaveFromDate = leaveTo
    ? new Date(leaveTo).toISOString().split("T")[0]
    : "";
  const leaveForValue = days === 0.5 ? 0.5 : 1;
  // Same From/To → single day, so Full/Half day is selectable; otherwise the
  // leave spans multiple days and is locked to Full Day.
  const isSingleDay = !!leaveFrom && leaveFrom === leaveTo;

  // Admin action is a decision on the request. The initial status is whatever the
  // employee submitted ("Submitted" on a fresh request); the admin must change it
  // (e.g. Approved / Rejected) before the update can be sent. This prevents
  // "submitting" the form while the status is still at its initial value.
  const initialStatus = selectedRow?.status || "Submitted";
  const statusChanged = !!selected?.value && selected.value !== initialStatus;

  // Read-only summary values (admin/approve view).
  const durationLabel = days === 0.5 ? "Half day" : `${formatDays(days)} ${days === 1 ? "day" : "days"}`;
  const lopDays = Number(selectedRow?.lopDays) || 0;
  const hasBalance = selectedRow?.currentBalance != null;

  const sharedSelectClassNames = {
    control: ({ isFocused, isDisabled }: any) =>
      `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? "!ring-2 !ring-blue-500 !border-blue-500" : ""
      } ${isDisabled
        ? "!bg-gray-50 dark:!bg-gray-700 !opacity-100"
        : ""
      }`,
    placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
    singleValue: () => "!text-gray-900 dark:!text-white !text-sm",
    menu: () =>
      "!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
    option: ({ isFocused, isSelected }: any) =>
      `!text-sm ${isSelected
        ? '!bg-primary !text-white'
        : isFocused
          ? '!bg-primary/10 !text-primary'
          : '!text-slate-700 dark:!text-slate-300'
      }`,
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col gap-3 px-3  pt-0">

        {isAdminView ? (
          /* ── Admin / approve view ───────────────────────────────────────────
             The employee's submission is read-only; the admin only acts on it
             (status / LOP / comment) below. */
          <>
            {/* Employee identity + current status */}
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-700/70">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                  {getInitials(selectedRow?.username)}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-slate-900 dark:text-white">
                    {selectedRow?.username || "—"}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {selectedRow?.email || "—"}
                  </p>
                </div>
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses(selectedRow?.status)}`}
              >
                {selectedRow?.status || "—"}
              </span>
            </div>

            {/* Submission details — compact key/value pairs (no bulky period
                banner) so the admin action controls stay visible without
                scrolling. The From → To period is now one readable item. */}
            <div className="grid grid-cols-3 gap-x-4 gap-y-3">
              <ReadItem
                label="Period"
                icon={<CalendarDays className="h-3 w-3" />}
                value={
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span>{dateFormate(selectedRow?.leaveFrom) || "—"}</span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
                    <span>{dateFormate(selectedRow?.leaveTo) || "—"}</span>
                  </span>
                }
              />
              <ReadItem label="Duration" icon={<Clock className="h-3 w-3" />} value={durationLabel} />
              <ReadItem label="Leave Type" icon={<Tag className="h-3 w-3" />} value={selectedRow?.leaveType || "—"} />
              <ReadItem
                label="Applied On"
                icon={<Clock className="h-3 w-3" />}
                value={selectedRow?.leaveAppliedDate ? dateFormate(selectedRow.leaveAppliedDate) : "—"}
              />

              <ReadItem
                label="Department"
                icon={<Briefcase className="h-3 w-3" />}
                value={<span className="capitalize">{selectedRow?.department || "—"}</span>}
              />
              {selectedRow?.adminEmail && (
                <ReadItem
                  label="Approved By"
                  icon={<UserCheck className="h-3 w-3" />}
                  value={selectedRow.adminEmail}
                />
              )}
              {hasBalance && (
                <ReadItem
                  label="Current Balance"
                  icon={<Wallet className="h-3 w-3" />}
                  value={`${formatDays(selectedRow.currentBalance)} days`}
                />
              )}
              {lopDays > 0 && (
                <ReadItem
                  label="Loss of Pay"
                  value={
                    <span className="text-rose-600 dark:text-rose-400">
                      {formatDays(lopDays)} {lopDays === 1 ? "day" : "days"}
                    </span>
                  }
                />
              )}
            </div>

            {/* Reason (read-only) */}
            {/* Reason — readable text (not an input-like box) to match the
                key/value summary above. */}
            <ReadItem label="Reason" value={selectedRow?.reason || "—"} />

            {/* ── Admin action ───────────────────────────────────────────── */}
            <div className="mt-1 border-t border-slate-100 pt-3 dark:border-slate-700/70">
              {/* <h4 className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                <UserCheck className="h-3.5 w-3.5" /> Admin Action
              </h4> */}

              {/* Balance warning — full width, shown above the action row so
                  the Status select and the LOP toggle can align side by side. */}
              {hasBalanceWarning && (
                <div className="mb-2.5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-900/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">{selectedRow.balanceWarning}</p>
                </div>
              )}

              {/* Status + Loss of Pay toggle in one row (LOP column only when the
                  leave is flagged; otherwise Status spans the full width). */}
              <div className={`grid items-start gap-3 ${hasBalanceWarning ? "sm:grid-cols-2" : "grid-cols-1"}`}>
                {/* Status */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Status <span className="text-error"> *</span>
                  </label>
                  <Select
                    classNames={sharedSelectClassNames}
                    {...methods.register("status", { required: true })}
                    options={data1}
                    placeholder="Select Status"
                    onChange={(selectedOption) => {
                      selectedOption &&
                        methods.setValue("status", selectedOption.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      if (selectedOption) setSelected(selectedOption);
                    }}
                    value={selected}
                    defaultValue={data1.find((option) => option.value === selectedRow.status)}
                  />
                  {methods.formState.errors.status && (
                    <span className="mt-0.5 block text-xs font-medium text-error">
                      {methods.formState.errors.status.message as ReactNode}
                    </span>
                  )}
                </div>

                {/* Loss of Pay (deductible) — only when the leave is flagged. The
                    control box is height-matched (min-h-[42px]) to the Status
                    select so both sit level in the row. */}
                {hasBalanceWarning && (
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Mark as Loss of Pay (deductible)
                    </label>
                    <div className="flex min-h-[42px] items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-900/20">
                      <span
                        className={`text-sm font-semibold ${isDeductible ? "text-amber-700 dark:text-amber-400" : "text-slate-500 dark:text-slate-400"}`}
                      >
                        {isDeductible ? "Yes" : "No"}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isDeductible}
                        aria-label="Mark as Loss of Pay (deductible)"
                        onClick={() => {
                          setIsDeductible((v) => !v);
                          setDeductibleTouched(true);
                        }}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${isDeductible ? "!bg-amber-500 border-amber-500" : "!bg-slate-200 border-slate-300 dark:!bg-slate-600 dark:border-slate-500"}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full !bg-white shadow-sm ring-1 ring-black/5 transition-transform ${isDeductible ? "translate-x-5" : "translate-x-0.5"}`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Comment */}
              <div className="mt-2">
                <Input
                  compact
                  type="textarea"
                  label="Comment"
                  placeholder="Enter comment..."
                  name="comment"
                  rows={2}
                  value={comment}
                  onChange={handleCommentChange}
                  className={sharedInputClassNames}
                />
              </div>
            </div>
          </>
        ) : (
          /* ── Edit view ──────────────────────────────────────────────────────
             The employee (or the admin on their own leave) edits the request. */
          <>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <Input
                compact
                name="leaveFrom"
                type="date"
                required
                label="Leave From"
                onChange={onLeaveFromChanged}
                value={leaveFrom}
                max={maxLeaveFromDate}
                disabled={isEmployeeView}
                className={sharedInputClassNames}
              />

              <div>
                <label className="mb-1 flex items-center justify-between gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <span>Leave Type <span className="text-error"> *</span></span>
                  {availableDays != null && (
                    <span
                      className={`text-xs font-normal ${overLimit
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-slate-500 dark:text-slate-400"}`}
                    >
                      {formatDays(availableDays)} {availableDays === 1 ? "day" : "days"} left
                    </span>
                  )}
                </label>
                <Select
                  classNames={sharedSelectClassNames}
                  options={leaveTypeSelectOptions}
                  {...methods.register("leaveType")}
                  name="leaveType"
                  onChange={onLeaveTypeChanged}
                  value={leaveTypeSelectOptions.find((op) => op.value === selectedLeaveType)}
                  isDisabled={isEmployeeView}
                />
                {methods.formState.errors.leaveType && (
                  <p className="mt-0.5 text-xs text-error">
                    {methods.formState.errors.leaveType.message as string}
                  </p>
                )}
              </div>

              <Input
                compact
                type="date"
                label="Leave To"
                required
                name="leaveTo"
                onChange={onLeaveToChanged}
                value={leaveTo}
                min={minLeaveToDate}
                disabled={isEmployeeView}
                className={sharedInputClassNames}
              />

              <Input
                compact
                type="number"
                label="Days"
                name="days"
                required
                value={days}
                onChange={onDaysChange}
                disabled={true}
                className={sharedInputClassNames}
              />

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Leave For <span className="text-error"> *</span>
                </label>
                <Select
                  classNames={sharedSelectClassNames}
                  options={isSingleDay ? leaveForOptions : [{ value: 1, label: "Full Day" }]}
                  name="leaveFor"
                  value={leaveForOptions.find((op) => op.value === leaveForValue)}
                  onChange={onLeaveForChanged}
                  isDisabled={isEmployeeView || !isSingleDay}
                />
              </div>
            </div>

            {/* Over-balance warning — only occupies space when actually shown,
                so no empty gap is reserved in the common (within-balance) case. */}
            {isOwnLeave && overLimit && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  You're applying <span className="font-semibold">{formatDays(days)}</span>{" "}
                  {days === 1 ? "day" : "days"}, but only{" "}
                  <span className="font-semibold">{formatDays(availableDays ?? 0)}</span>{" "}
                  {availableDays === 1 ? "day is" : "days are"} available for{" "}
                  <span className="font-semibold">{selectedLeaveType}</span>. You can still submit.
                </span>
              </div>
            )}

            <div>
              {isEmployeeView ? (
                /* Read-only viewer → readable text, not a disabled input box. */
                <ReadItem label="Reason" value={reason || selectedRow?.reason || "—"} />
              ) : (
                <Input
                  compact
                  type="textbox"
                  label="Leave Reason"
                  placeholder="Enter your reason for leave..."
                  name="reason"
                  required
                  value={reason}
                  onChange={handleReasonChange}
                  className={sharedInputClassNames}
                />
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className=" flex justify-end gap-2  dark:border-slate-800">
          <Button
            onClick={onClose}
            type="button"
            className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              (isAdminView
                ? // Admin must actually change the status (or toggle LOP on a
                  // flagged leave) — a comment edit alone no longer enables submit.
                  !(statusChanged || deductibleTouched)
                : !isDirty && !deductibleTouched)
            }
            className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800 disabled:!cursor-not-allowed disabled:!opacity-60"
          >
            {isAdminView ? "Update Status" : "Update"}
          </Button>
        </div>

      </form>
    </FormProvider>
  );
};

export default LeaveDetails;